/* eslint-disable @typescript-eslint/naming-convention */
import * as Sentry from '@sentry/vue';
import type {App} from 'vue';
import type {Router} from 'vue-router';
import type {SentryHttpContext, SentryBreadcrumbData, SentrySeverityLevel} from '@/ts/types/sentry';
import {loggerInstance} from '@/ts/instances/logger-instance';
import {SENTRY, GIT_HASH} from '@/ts/utils/consts';
import {isExpectedAppError} from '@/ts/utils/errors';

// Credentials the app puts in a query: the emailed verification link (?userID=...&code=... can set the
// account password), the OIDC callback (?code=...), the token request (code_verifier, and refresh_token,
// which renews the session on its own) and the logout (id_token_hint). None may leave the app through telemetry.
const sensitiveQueryParamRegex = /(?<param>(?:^|[?&])(?:code|code_verifier|refresh_token|id_token_hint|userID))=[^&#]*/giu;

// The router integration records each query parameter as an attribute of its own, holding the value alone.
const sensitiveQueryAttributeRegex = /^query\.(?:code|code_verifier|refresh_token|id_token_hint|userID)$/iu;

// The SDK's normalizeDepth: Sentry sends anything deeper as "[Object]", so copying further is wasted work.
const maxScrubDepth = 3;

function filterSensitiveParams(url: string): string {
  // ?. appeases sonarjs/null-dereference — its flow analysis doesn't trust the param type.
  return url?.replace(sensitiveQueryParamRegex, '$<param>=[Filtered]') ?? '';
}

// Sentry sends a Vue instance or VNode as "[VueViewModel]" and never reads into it.
function isVueViewModel(value: object): boolean {
  return '__isVue' in value || '_isVue' in value || '__v_isVNode' in value;
}

function scrubCopy(value: unknown, depth: number, ancestors: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    return filterSensitiveParams(value);
  }
  if (!value || typeof value !== 'object' || depth >= maxScrubDepth || ancestors.has(value) || isVueViewModel(value)) {
    return value;
  }
  // Sentry serializes a URL through toJSON, as its href.
  if (value instanceof URL) {
    return filterSensitiveParams(value.href);
  }
  const child = (item: unknown): unknown => scrubCopy(item, depth + 1, ancestors);
  const entries = (source: object): Record<string, unknown> => Object.fromEntries(
    Object.entries(source).map(([key, item]) => [key, sensitiveQueryAttributeRegex.test(key) ? '[Filtered]' : child(item)]),
  );
  ancestors.add(value);
  let copy: unknown;
  if (Array.isArray(value)) {
    copy = value.map(child);
  } else if (value instanceof Error) {
    copy = {
      ...entries(value),
      name: value.name,
      message: filterSensitiveParams(value.message),
      stack: value.stack && filterSensitiveParams(value.stack),
    };
  } else {
    copy = entries(value);
  }
  ancestors.delete(value);
  return copy;
}

/**
 * Returns a copy with every string scrubbed, and the attributes that hold a sensitive query parameter alone
 * blanked. Every string rather than known keys: the SDK keeps adding places for the URL. Objects become plain
 * copies of their own strings, as Sentry would send them; it never writes into what it is given, and stops at
 * cycles, at Vue instances and at the SDK's depth.
 */
function scrubbed<T>(value: T): T {
  return scrubCopy(value, 0, new WeakSet()) as T;
}

/** Replaces sensitive query-param values with [Filtered] in absolute or relative URLs. */
export function scrubSensitiveUrl(url: string): string {
  return filterSensitiveParams(url);
}

/** Scrubs the URL-bearing fields Sentry attaches to error and transaction events. */
export function scrubEventRequest<T extends {request?: {url?: string; headers?: Record<string, string>}}>(event: T): T {
  if (event.request?.url) {
    event.request.url = scrubSensitiveUrl(event.request.url);
  }
  if (event.request?.headers?.Referer) {
    event.request.headers.Referer = scrubSensitiveUrl(event.request.headers.Referer);
  }
  return event;
}

/** Scrubs the URLs that breadcrumbs carry: navigation and fetch data, and console messages and arguments. */
export function scrubBreadcrumbUrls(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  if (breadcrumb.message) {
    breadcrumb.message = filterSensitiveParams(breadcrumb.message);
  }
  if (breadcrumb.data) {
    breadcrumb.data = scrubbed(breadcrumb.data);
  }
  return breadcrumb;
}

/**
 * Scrubs the URLs that events carry outside the request: the extras of captured errors, and the trace
 * context and spans, where page-load transactions put the window URL and fetch spans their query.
 */
export function scrubEventData<
  T extends {
    extra?: Record<string, unknown>;
    contexts?: {trace?: {data?: Record<string, unknown>}};
    spans?: {description?: string; data?: Record<string, unknown>}[];
  },
>(event: T): T {
  if (event.extra) {
    event.extra = scrubbed(event.extra);
  }
  if (event.contexts?.trace?.data) {
    event.contexts.trace.data = scrubbed(event.contexts.trace.data);
  }
  for (const span of event.spans ?? []) {
    if (span.description) {
      span.description = filterSensitiveParams(span.description);
    }
    if (span.data) {
      span.data = scrubbed(span.data);
    }
  }
  return event;
}

/** Manages Sentry error tracking initialization, breadcrumbs, and error reporting. */
export class SentryService {
  private readonly logger = loggerInstance.getLogger('sentry');
  private initialized = false;

  // Check if Sentry is configured
  private isSentryConfigured(): boolean {
    return Boolean(SENTRY?.DSN);
  }

  // Error tracking is enabled for all users (error monitoring is separate from analytics)
  private isErrorTrackingEnabled(): boolean {
    return true;
  }

  // Check if Sentry is ready to be used (configured + initialized + user opted in)
  private isReady(): boolean {
    return this.isSentryConfigured() && this.initialized && this.isErrorTrackingEnabled();
  }

  /**
   * Initialize Sentry BEFORE Vue app is created.
   * Must be called early in main.ts before app.mount().
   */
  initializeSentry(app: App, router: Router): void {
    if (this.initialized || !this.isSentryConfigured()) {
      this.logger.debug(
        'Sentry not initialized: already initialized=',
        this.initialized,
        'or not configured=',
        !this.isSentryConfigured(),
      )();
      return;
    }

    const projectId = new URL(SENTRY!.DSN).pathname.slice(1);
    const tunnel = `https://sentry-tunnel.significo.dev/api/${projectId}/envelope/`;

    Sentry.init({
      app,
      dsn: SENTRY!.DSN,
      environment: SENTRY!.ENVIRONMENT,
      release: GIT_HASH,
      tunnel,

      // Performance monitoring
      tracesSampleRate: SENTRY!.TRACES_SAMPLE_RATE ?? 1.0,

      // Integrations
      integrations: [
        Sentry.browserTracingIntegration({router}),
// Capture breadcrumbs for debugging (no DOM/user interactions for privacy)
        Sentry.breadcrumbsIntegration({
          console: true, // Console logs as breadcrumbs (not issues)
          dom: false, // Disabled for privacy - no user interaction tracking
          fetch: true, // Track fetch requests for debugging API issues
          history: true, // Track navigation for debugging
          xhr: true, // Track XHR requests for debugging API issues
        }),
      ],

      // Global tags matching backend
      initialScope: {
        tags: {
          'app.version': GIT_HASH,
          'app.name': 'intervention-platform-web',
        },
      },

      // Filter sensitive data
      beforeSend(event) {
        // Remove potentially sensitive information
        if (event.request?.headers) {
          delete event.request.headers.Authorization;
        }
        return scrubEventData(scrubEventRequest(event));
      },

      // Pageload/navigation transactions attach window.location.href too
      beforeSendTransaction(event) {
        return scrubEventData(scrubEventRequest(event));
      },

      // Breadcrumb filtering
      beforeBreadcrumb(breadcrumb) {
        // Filter out noisy or sensitive breadcrumbs
        if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
          return null;
        }
        return scrubBreadcrumbUrls(breadcrumb);
      },
    });

    this.initialized = true;
    this.logger.debug('Sentry initialized with environment:', SENTRY!.ENVIRONMENT)();
  }

  // Set HTTP context for API requests (matching backend pattern)
  setHttpContext(context: SentryHttpContext): void {
    if (!this.isReady()) {
      return;
    }

    Sentry.setContext('http', context);
  }

  // Add custom tag
  setTag(key: string, value: string): void {
    if (!this.isReady()) {
      return;
    }

    Sentry.setTag(key, value);
  }

  // Add breadcrumb (matching backend HTTP request/response lifecycle)
  addBreadcrumb(breadcrumb: SentryBreadcrumbData): void {
    if (!this.isReady()) {
      return;
    }

    Sentry.addBreadcrumb({
      category: breadcrumb.category,
      message: breadcrumb.message,
      level: breadcrumb.level ?? 'info',
      data: breadcrumb.data,
    });
  }

  /**
   * Capture exception manually.
   * Expected business outcomes are dropped here rather than at each call site: the rule reached
   * five call sites in three shapes, and every site added was a site that could be forgotten —
   * getBlob and use-validate-voucher never got one. Use captureAlways for the rare caller that
   * must report regardless.
   */
  captureException(error: Error, context?: Record<string, unknown>): void {
    if (isExpectedAppError(error)) {
      return;
    }
    this.captureAlways(error, context);
  }

  /**
   * Report even when the error carries an expected business code. Only for callers where reaching
   * them is itself the fault: Vue's errorHandler means the render path broke, and a blanked page
   * must not be invisible in Sentry just because the underlying code is a business one.
   */
  captureAlways(error: Error, context?: Record<string, unknown>): void {
    if (!this.isReady()) {
      this.logger.debug('Sentry not ready, skipping error capture:', error.message)();
      return;
    }

    if (context) {
      Sentry.withScope((scope) => {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
        Sentry.captureException(error);
      });
    } else {
      Sentry.captureException(error);
    }

    this.logger.debug('Exception captured by Sentry:', error.message)();
  }

  // Capture message
  captureMessage(message: string, level: SentrySeverityLevel = 'info'): void {
    if (!this.isReady()) {
      return;
    }

    Sentry.captureMessage(message, level);
  }

  // Capture log entry with category and optional data
  captureLog(
    category: string,
    message: string,
    level: SentrySeverityLevel = 'info',
    data?: Record<string, unknown>,
  ): void {
    if (!this.isReady()) {
      return;
    }

    // Add as breadcrumb for context in future errors
    Sentry.addBreadcrumb({
      category,
      message,
      level,
      data,
    });

    // For warning and error levels, also capture as a message event
    if (level === 'warning' || level === 'error') {
      Sentry.withScope((scope) => {
        scope.setTag('log.category', category);
        if (data) {
          Object.entries(data).forEach(([key, value]) => {
            scope.setExtra(key, value);
          });
        }
        Sentry.captureMessage(`[${category}] ${message}`, level);
      });
    }
  }

  // Extract intervention ID from URL path (matching backend pattern)
  extractInterventionIdFromPath(path: string): string | undefined {
    // Match patterns like /path-{uuid}/... or /intervention/{uuid}/...
    const pathRegex = /path-(?<id>[a-f0-9-]{36})/iu;
    const interventionRegex = /intervention\/(?<id>[a-f0-9-]{36})/iu;
    const interventionMatch = pathRegex.exec(path) ?? interventionRegex.exec(path);
    return interventionMatch?.groups?.id;
  }
}

/** Singleton Sentry error tracking instance with tunneled DSN, performance monitoring, and privacy filtering */
export const sentryService = new SentryService();
